import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import parseMax from 'libphonenumber-js/mobile';
import Skeleton from 'react-loading-skeleton';

import { FormInput } from '../FormInput/FormInputViewTwo';
import emailIcon from '../../assets/icons/email.svg';
import { PhoneFormInput } from '../PhoneFormInput/PhoneFormInput';
import { useWidth } from '../../hooks/useWidth';
import { restCountries } from '../../constants/restCountries';
import { Dropdown } from '../Dropdown/DropdownViewTwo';
import { FormCheckbox } from '../FormCheckbox/FormCheckboxViewTwo';
import { useSafeSelector } from '../../hooks/useSafeSelector';
import {
  ContactFormAddress,
  ContactFormValues,
  FlattenedContactFormValues,
  IContactFormProps,
} from './types';
import {
  FIELD_TYPES,
  flattenContactFormValues,
  getAdditionalFields,
  getCountryFromPhoneNumber,
} from './utils';
export * from './types';

export type RestCountryType = Partial<(typeof restCountries)[0]>;

const ADDRESS_TYPES = { SHIPPING: 'shipping', BILLING: 'billing' } as const;
type AddressType = (typeof ADDRESS_TYPES)[keyof typeof ADDRESS_TYPES];

export const ContactForm = forwardRef((props: IContactFormProps, ref) => {
  const {
    onClientData,
    collectShippingAddress,
    collectBillingAddress,
    needsName,
    needsEmail,
    needsPhone,
    emailRequired = true,
    phoneRequired = true,
    nameRequired = true,
    needsPurpose,
    defaultCountryISO = 'EG',
    defaultValues,
    hideLabels,
    onValidityChange,
    disableFields,
    disableDropdown,
    verifiedFields,
    allowedCountries,
    sortByAllowedCountries,
    isFastCheckoutEnabled,
    mode = "onChange",
    regionDataBaseUrl = '/region-data',
  } = props;
  const lang = useSafeSelector((state: any) => state.lang);
  const langDirection = lang === 'ar' ? 'rtl' : 'ltr';
  const isRTL = langDirection === "rtl";

  const {
    register,
    watch,
    formState: { errors, isDirty, isValid, dirtyFields,isValidating },
    setValue,
    trigger,
    clearErrors,
    control,
    getValues,
    resetField
  } = useForm({
    mode,
    shouldUnregister: true,
    defaultValues: flattenContactFormValues(defaultCountryISO, defaultValues),
  });

const countriesSource = useMemo(() => {
  if (allowedCountries && allowedCountries.length > 0) {
    return restCountries.filter((c) => allowedCountries.includes(c.iso));
  }
  return restCountries;
}, [allowedCountries, restCountries]);

const {
  defaultPhoneCountry,
  defaultShippingCountry,
  defaultBillingCountry,
  defaultShippingPhoneCountry,
  defaultBillingPhoneCountry,
} = useMemo(() => {
  const fallback =
    countriesSource.find((c) => c.iso === defaultCountryISO) ||
    countriesSource[0] ||
    null;

  // `allowedCountries` only restricts shipping, so billing must not inherit a
  // shipping-allowed country when the checkout country is outside that list.
  const billingFallback =
    restCountries.find((c) => c.iso === defaultCountryISO) || fallback;

  const phoneCountry = getCountryFromPhoneNumber(defaultValues?.phone);
  const shippingPhoneCountry = getCountryFromPhoneNumber(
    defaultValues?.shipping?.phone_number
  );
  const billingPhoneCountry = getCountryFromPhoneNumber(
    defaultValues?.billing?.phone_number
  );

  const shippingAddressCountry = defaultValues?.shipping?.country
    ? countriesSource.find(
        (c) => c.iso === defaultValues.shipping?.country
      ) || null
    : null;
    const notAllowedShippingCountry = allowedCountries?.length && defaultValues?.shipping && !allowedCountries.includes( defaultValues?.shipping?.country || "" )
    const updatedShippinCountry = notAllowedShippingCountry ? null : ( shippingAddressCountry || shippingPhoneCountry || fallback) 
  const billingAddressCountry = defaultValues?.billing?.country
    ? countriesSource.find((c) => c.iso === defaultValues.billing?.country) ||
      null
    : null;

  return {
    defaultPhoneCountry: phoneCountry,
    defaultShippingCountry:
      updatedShippinCountry,
    defaultBillingCountry:
      billingAddressCountry || billingPhoneCountry || billingFallback,
    defaultShippingPhoneCountry:
      shippingPhoneCountry || shippingAddressCountry || fallback,
    defaultBillingPhoneCountry:
      billingPhoneCountry || billingAddressCountry || billingFallback,
  };
}, [countriesSource, defaultCountryISO, JSON.stringify(defaultValues)]);

  const { t } = useTranslation('kitComponents');
  const isMobile = useWidth();
  const [country, setCountry] = useState<RestCountryType | null>(
    defaultPhoneCountry || restCountries.find((c) => c.iso === defaultCountryISO) || null
  );
  const [shippingCountry, setShippingCountry] = useState<RestCountryType | null>(
    defaultShippingCountry!
  );
  const [shippingPhoneCountry, setShippingPhoneCountry] =
    useState<RestCountryType | null>(defaultShippingPhoneCountry!);
  const [billingCountry, setBillingCountry] = useState<RestCountryType | null>(
    defaultBillingCountry!
  );
  const [billingPhoneCountry, setBillingPhoneCountry] =
    useState<RestCountryType | null>(defaultBillingPhoneCountry!);
  // Region data (states/cities) is fetched per selected country on demand and
  // cached here, keyed by ISO code, instead of bundling the full dataset.
  const [regionData, setRegionData] = useState<Record<string, any>>({});
  const [loadingISOs, setLoadingISOs] = useState<string[]>([]);

  const countriesOptions = useMemo(() => {
    let filteredCountries = restCountries;
    if (allowedCountries?.length) {
      filteredCountries = restCountries.filter((country) =>
        allowedCountries.includes(country.iso)
      );

      if (sortByAllowedCountries) {
        filteredCountries = [...filteredCountries].sort((a, b) => {
          return (
            allowedCountries.indexOf(a.iso) - allowedCountries.indexOf(b.iso)
          );
        });
      }
    }

    return filteredCountries.map((country) => ({
      label: country.name,
      value: country.iso,
      icon: country.icon,
      code: country.code,
    }));
  }, []);

  const watchFields = watch();

  useImperativeHandle(ref, () => ({
    getIsValid: () => isValid,
    trigger: () => trigger(),
    getFormFields: () => ({ ...watchFields, country }),
  }));

  const watchFieldsJson = useMemo(
    () => JSON.stringify(watchFields),
    [watchFields]
  );

  useEffect(() => {
    if (onClientData && isValid && (isDirty || defaultValues)) {
      const data: Partial<ContactFormValues> = {};

      for (const key in watchFields) {
        const value = watchFields[key as keyof typeof watchFields];
        if (key.startsWith('shipping_')) {
          const field = key.replace(
            'shipping_',
            ''
          ) as keyof ContactFormAddress;
          if (!data.shipping) data.shipping = {} as ContactFormAddress;
          data.shipping![field as keyof ContactFormAddress] = value as string;
          if (field === 'phone_number') {
            data.shipping.phone_number = shippingPhoneCountry?.code! + value;
          }
        } else if (key.startsWith('billing_')) {
          const field = key.replace('billing_', '') as keyof ContactFormAddress;
          if (!data.billing) data.billing = {} as ContactFormAddress;
          data.billing![field as keyof ContactFormAddress] = value as string;
          if (field === 'phone_number') {
            data.billing.phone_number = billingPhoneCountry?.code! + value;
          }
        } else {
          (data as any)[key] = value;
          if (key === 'phone') {
            data.phone = country?.code! + value;
          }
        }
      }
      onClientData(data);
    } else if (onClientData && isDirty && !isValid) {
      // trigger if email is valid and fast checkout is enabled to handle auto-login 
        if (isFastCheckoutEnabled && !isValidating) {
        const email = watchFields.email
        if(email && !errors.email ){
         onClientData({email})
        }
      }
       else {onClientData(null);}
    }
    
    const checkIsChanged = () => {
      const currentValues = getValues();
      let isChanged = false;

      for (const key in currentValues) {
        let currentFieldValue =
          currentValues[key as keyof typeof currentValues];
        let defaultValue = defaultValues?.[key as keyof typeof defaultValues]; 
        if (currentFieldValue === '' && defaultValue === undefined) continue;

        if (
          typeof currentFieldValue === 'string' &&
          typeof defaultValue === 'string'
        ) {
          currentFieldValue = currentFieldValue.trimEnd();
          defaultValue = defaultValue.trimEnd();
        }
        if (currentFieldValue !== defaultValue && dirtyFields[key as keyof typeof currentValues]) {
          return isChanged = true;
        }      
      }
      return isChanged;
    };

    onValidityChange?.(
      isValid && (isDirty || !!defaultValues),!!checkIsChanged()
    );

  }, [isDirty, isValid, watchFieldsJson, isValidating, Object.keys(dirtyFields).length]);

  useEffect(() => {
    if (!collectBillingAddress && !collectShippingAddress) return;

    const isos = new Set<string>();
    if (collectShippingAddress && shippingCountry?.iso)
      isos.add(shippingCountry.iso);
    if (collectBillingAddress && billingCountry?.iso)
      isos.add(billingCountry.iso);

    isos.forEach((iso) => {
      if (regionData[iso] || loadingISOs.includes(iso)) return;
      setLoadingISOs((prev) => [...prev, iso]);
      fetch(`${regionDataBaseUrl}/${iso}.json`)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (json) setRegionData((prev) => ({ ...prev, [iso]: json }));
        })
        .catch(() => {
          /* leave the country unloaded; dropdowns simply won't render */
        })
        .finally(() => {
          setLoadingISOs((prev) => prev.filter((i) => i !== iso));
        });
    });
  }, [
    collectBillingAddress,
    collectShippingAddress,
    shippingCountry?.iso,
    billingCountry?.iso,
  ]);

  useEffect(() => {
  if (!defaultCountryISO || !allowedCountries?.length) return;

  if (allowedCountries.includes(defaultCountryISO)) {
    if (collectShippingAddress && !defaultValues?.shipping?.phone_number) {
      setValue("shipping_country", defaultCountryISO, { shouldValidate: true, shouldDirty: true });
      setShippingCountry(
        countriesSource.find((c) => c.iso === defaultCountryISO) || null
      );
      setShippingPhoneCountry(
        countriesSource.find((c) => c.iso === defaultCountryISO) || null
      );
    }

    if (collectBillingAddress && !defaultValues?.billing?.phone_number) {
      setValue("billing_country", defaultCountryISO, { shouldValidate: true, shouldDirty: true });
      setBillingCountry(
        countriesSource.find((c) => c.iso === defaultCountryISO) || null
      );
      setBillingPhoneCountry(
        countriesSource.find((c) => c.iso === defaultCountryISO) || null
      );
    }
  }
}, [
  defaultCountryISO,
  allowedCountries,
  collectShippingAddress,
  collectBillingAddress,
  countriesSource,
  setValue
]);

  useEffect(() => {
    if (needsPhone && (dirtyFields.phone || getValues('phone'))) {
      trigger('phone');
    }
  }, [country?.code]);

  useEffect(() => {
    if (
      collectShippingAddress &&
      (dirtyFields.shipping_phone_number || getValues('shipping_phone_number'))
    ) {
      trigger('shipping_phone_number');
    }
  }, [shippingPhoneCountry?.code]);

  useEffect(() => {
    if (
      collectBillingAddress &&
      (dirtyFields.billing_phone_number || getValues('billing_phone_number'))
    ) {
      trigger('billing_phone_number');
    }
  }, [billingPhoneCountry?.code]);

  const createEmailRegister = useCallback(
    ({
      field = 'email',
    }: { field?: keyof FlattenedContactFormValues } = {}) => {
      return register(field, {
        onChange: (e) => {
          clearErrors(field);
          if (!e.target.value) {
            setValue(field, '', { shouldValidate: false });
          } else {
            setValue(field, e.target.value);
          }
        },
        required: emailRequired
          ? {
              value: true,
              message: t('EMAIL_IS_REQUIRED'),
            }
          : false,
        validate: (value: any) => {
          if (!value) return !emailRequired || t('EMAIL_IS_REQUIRED');
          const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
          return regex.test(value) || t('EMAIL_NOT_VALID');
        },
      });
    },
    []
  );

  const createFullNameRegister = useCallback(
    ({ field = 'name' }: { field?: keyof FlattenedContactFormValues } = {}) => {
      return register(field, {
        onChange: (e) => {
          clearErrors(field);
          if (!e.target.value) {
            setValue(field, '', { shouldValidate: false });
          } else {
            const name = e.target.value;
            const nameRegex = /^((?:[\p{L}\p{M}]+ ?){1,3})$/u;
            const displayValue = name
              .replace(/[^\p{L}\p{M} ]/gu, '')
              .replace(/\s+/g, ' ');

            if (nameRegex.test(displayValue)) {
              if (/^[A-Za-z ]+$/.test(displayValue)) {
                setValue(field, displayValue.toUpperCase());
              } else {
                setValue(field, displayValue);
              }
            } else {
              if (/^[A-Za-z ]+$/.test(displayValue.trim())) {
                setValue(field, displayValue.trim().toUpperCase());
              } else {
                setValue(field, displayValue.trim());
              }
            }
          }
        },
        required: nameRequired
        ? {
            value: true,
            message: t('NAME_IS_REQUIRED'),
          }
        : false,
      });
    },
    []
  );

  const createPhoneRegister = useCallback(
    ({
      field = 'phone',
      countryCode,
    }: {
      field?: keyof FlattenedContactFormValues;
      countryCode: string;
    }) => {
      return register(field, {
        onChange: (e) => {
          const phoneNumber = e.target.value;
          const trimmedNumber = phoneNumber.replace(/\D/g, '');
          setValue(field, trimmedNumber);
        },
        required: phoneRequired
          ? {
              value: true,
              message: t('PHONE_IS_REQUIRED'),
            }
          : false,
        validate: (value) => {
          if (!value) return !phoneRequired || t('PHONE_IS_REQUIRED');
          const validPhone = parseMax(countryCode + value);
          return (
            ((validPhone?.getType() === 'MOBILE' || validPhone?.getType() === 'FIXED_LINE_OR_MOBILE') && validPhone?.isValid()) ||
            t('PHONE_NOT_VALID')
          );
        },
      });
    },
    []
  );

  const createPurposeRegister = useCallback(
      ({ field = 'purpose' }: { field?: keyof FlattenedContactFormValues }={}) => {
        return register(field, {
          onChange: (e) => {
            clearErrors(field);
            if (!e.target.value) {
              setValue(field, '', { shouldValidate: false });
            } else {
              setValue(field, e.target.value);
            }
          },
          required: {
            value: true,
            message: t('PURPOSE_IS_REQUIRED'),
          },
        });
      },
      []
  );

  const getInputClasses = (
    fieldName: keyof FlattenedContactFormValues,
    position: 'top' | 'middle' | 'bottom' | 'standalone' | 'left' | 'right'
  ) => {
    const hasError = errors[fieldName];

    let classes = 'english-label-regular !border ';

    if (position === 'standalone') {
      classes += '!rounded-lg ';
    } else {
      classes += '!rounded-none ';
    }

    if (position === 'top') {
      classes += '!rounded-t-lg ';
    } else if (position === 'bottom') {
      classes += '!rounded-b-lg ';
    }

    if (position === 'middle' || position === 'bottom') {
      classes += '-mt-px ';
    }

    if (position === 'left') {
      classes += isRTL ? '!rounded-r-lg ' : '!rounded-l-lg ';
    } else if (position === 'right') {
      classes +=  isRTL ? '!rounded-l-lg ' : '!rounded-r-lg ';
    }

    if (position === 'right') {
      classes += isRTL ? '-me-px ' : '-ms-px ';
    }

    if (hasError) {
      classes += '!z-[1] !border-red-300 ';
    } else {
      classes += '!border-gray-300 ';
    }

    return classes;
  };

  const handleCountryChange = (
    country: (typeof countriesOptions)[0],
    addressType: AddressType
  ) => {
    const { code, label, value } = country;
    const updatedCountry = { name: label, iso: value, code };

    if (addressType === ADDRESS_TYPES.SHIPPING) {
      setShippingCountry(updatedCountry);
    } else {
      setBillingCountry(updatedCountry);
    }

    const additionalFields = getAdditionalFields(value);
    additionalFields?.forEach(({ field }) => {
      const fieldName =
        `${addressType}_${field}` as keyof FlattenedContactFormValues;
      resetField(fieldName);
    });
  };

  const renderAddressSection = (prefix: AddressType) => {
    const countryField = `${prefix}_country` as const;
    const country =
      (prefix === ADDRESS_TYPES.SHIPPING ? shippingCountry : billingCountry) ||
      ({} as RestCountryType);
    const countryISO = country.iso!;
    const additionalFields = getAdditionalFields(countryISO);

    return (
      <div className="flex flex-col gap-2">
        {!hideLabels && (
          <p className="text-gray-700 capitalize english-label-medium">
            {t(`${prefix.toUpperCase()}_ADDRESS`)}
          </p>
        )}
        <div className="flex flex-col">
          <FormInput
            formRegister={createFullNameRegister({
              field: `${prefix}_full_name`,
            })}
            name={`${prefix}_full_name`}
            error={errors[`${prefix}_full_name`]}
            className={getInputClasses(`${prefix}_full_name`, 'top')}
            placeholder="ex. Abdullah Adel"
            customStyle={{ direction: langDirection }}
          />
          <PhoneFormInput
            name={`${prefix}_phone_number`}
            error={errors[`${prefix}_phone_number`]}
            selectedCountry={{
              name:
                prefix === ADDRESS_TYPES.SHIPPING
                  ? shippingPhoneCountry?.name!
                  : billingPhoneCountry?.name!,
              code:
                prefix === ADDRESS_TYPES.SHIPPING
                  ? shippingPhoneCountry?.code!
                  : billingPhoneCountry?.code!,
              // without the iso, PhoneFormInput cannot tell that the current
              // country is allowed and resets it to the first allowed one
              iso:
                prefix === ADDRESS_TYPES.SHIPPING
                  ? shippingPhoneCountry?.iso!
                  : billingPhoneCountry?.iso!,
            }}
            className={getInputClasses(`${prefix}_phone_number`, 'middle')}
            setSelectedCountry={(country: any) =>
              prefix === ADDRESS_TYPES.SHIPPING
                ? setShippingPhoneCountry(country)
                : setBillingPhoneCountry(country)
            }
            phoneFormRegister={createPhoneRegister({
              field: `${prefix}_phone_number`,
              countryCode:
                prefix === ADDRESS_TYPES.SHIPPING
                  ? shippingPhoneCountry?.code!
                  : billingPhoneCountry?.code!,
            })}
            placeholder="10 000 000 00"
            allowedCountries={
              (prefix === ADDRESS_TYPES.SHIPPING && Array.isArray(allowedCountries) && allowedCountries.length)? allowedCountries : undefined
            }
            sortByAllowedCountries={prefix === ADDRESS_TYPES.SHIPPING}
            disableDropdown={disableDropdown?.phone}
            disabled={disableFields?.phone}
          />
          <Controller
            name={countryField}
            control={control}
            defaultValue={prefix === ADDRESS_TYPES.SHIPPING ? defaultShippingCountry?.iso : defaultBillingCountry?.iso || ''}
            rules={{ required: t('COUNTRY_IS_REQUIRED') }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                label={t('COUNTRIES')}
                onChange={(selected: any) => {
                  if (selected.value !== value) {
                    onChange(selected.value);
                    handleCountryChange(selected, prefix);
                    const mappedSelected = restCountries.find((c) => c.iso === selected.value)
                    if(mappedSelected){
                      prefix === ADDRESS_TYPES.SHIPPING ? setShippingPhoneCountry(mappedSelected)
                      : setBillingPhoneCountry(mappedSelected)
                    }
                  }
                }}
                options={
                  prefix === ADDRESS_TYPES.SHIPPING
                    ? countriesOptions
                    : restCountries.map((country) => ({
                        label: country.name,
                        value: country.iso,
                        icon: country.icon,
                        code: country.code,
                      }))
                }
                defaultOption={value || (prefix === ADDRESS_TYPES.SHIPPING ? defaultShippingCountry?.iso : defaultBillingCountry?.iso ) || ''}
                className={`!shadow-none !border-b-0 ${getInputClasses(
                  `${prefix}_country`,
                  'middle'
                )}`}
                hideCircleIcon
                hideRemoveOptionBtn
              />
            )}
          />

          <FormInput
            formRegister={register(`${prefix}_address_line_1`, {
              required: {
                value: true,
                message: t('ADDRESS_IS_REQUIRED'),
              },
            })}
            name={`${prefix}_address_line_1`}
            error={errors[`${prefix}_address_line_1`]}
            placeholder={t('ADDRESS_1')}
            className={getInputClasses(`${prefix}_address_line_1`, 'middle')}
            customStyle={{ direction: langDirection }}
          />
          <FormInput
            formRegister={register(`${prefix}_address_line_2`)}
            name={`${prefix}_address_line_2`}
            placeholder={t('ADDRESS_2')}
            className={getInputClasses(`${prefix}_address_line_2`, 'middle')}
            customStyle={{ direction: langDirection }}
          />
          {additionalFields.map(({ name, field, type, isRequired }, idx) => {
            const label = t(name.toUpperCase());
            const position =
              additionalFields.length - 1 === idx || countryISO === 'AE'
                ? 'bottom'
                : 'middle';
            const fieldName =
              `${prefix}_${field}` as keyof FlattenedContactFormValues;

            if (type === FIELD_TYPES.TEXT) {
              return (
                <FormInput
                  key={field}
                  formRegister={register(fieldName, {
                    required:
                      isRequired && t(`${name.toUpperCase()}_IS_REQUIRED`),
                  })}
                  name={fieldName}
                  error={errors[fieldName]}
                  placeholder={label}
                  className={getInputClasses(fieldName, position)}
                  customStyle={{ direction: langDirection }}
                />
              );
            } else {
              if (loadingISOs.includes(countryISO)) {
                return (
                  <div key={field} className="pb-1">
                    <Skeleton height={40} />
                  </div>
                );
              }

              const countryRegion = countryISO ? regionData[countryISO] : null;
              if (!countryRegion) return null;

              const statesList = Object.keys(countryRegion.states).map(
                (state) => ({ label: state, value: state })
              );
              const selectedCountryState = watchFields[`${prefix}_state`]!;
              const citiesList = countryRegion.states[
                selectedCountryState
              ]?.cities.map((city: string) => ({ label: city, value: city }));

              const options = field === 'state' ? statesList : citiesList;

              if (!options?.length) return null;

              const key =
                field === 'state'
                  ? countryISO + field
                  : countryISO + selectedCountryState + field;

              return (
                <Controller
                  key={key}
                  name={fieldName}
                  control={control}
                  rules={{
                    required:
                      isRequired && t(`${name.toUpperCase()}_IS_REQUIRED`),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      label={label}
                      onChange={(selected: any) => {
                        if (selected.value !== value) {
                          onChange(selected.value);
                          const fieldName =
                            `${prefix}_${field}` as keyof FlattenedContactFormValues;
                          if (fieldName.includes('state')) {
                            setValue(`${prefix}_city`, '', {
                              shouldValidate: false,
                            });
                            setValue(`${prefix}_postal_code`, '', {
                              shouldValidate: false,
                            });
                          } else if (fieldName.includes('city')) {
                            setValue(`${prefix}_postal_code`, '', {
                              shouldValidate: false,
                            });
                          }
                        }
                      }}
                      options={options}
                      className={`!shadow-none ${
                        additionalFields.length - 1 === idx ? '' : '!border-b-0'
                      } ${getInputClasses(fieldName, position)}`}
                      hideCircleIcon
                      hideRemoveOptionBtn
                      defaultOption={value as string}
                      disableDropdown={!getValues(countryField)}
                    />
                  )}
                />
              );
            }
          })}
        </div>
      </div>
    );
  };

  if (collectShippingAddress || collectBillingAddress) {
    const ErrorList = ({ addressType }: { addressType: string }) => {
      const errorsList = Object.entries(errors)
        .filter(([i]) => i.includes(addressType))
        .map(([_, e]) => e);

      return (
        !!errorsList.length && (
          <div className="flex flex-col gap-1 text-red-600 english-helper-text-regular">
            {errorsList?.map((error, idx) => (
              <p className="w-1/2" key={idx}>
                {error?.message}
              </p>
            ))}
          </div>
        )
      );
    };

    return (
      <form className="flex flex-col gap-4">
        {(needsEmail || needsPhone) && (
          <div className="flex flex-col">
            {!hideLabels && (
              <p
                className={`mb-2 text-sm font-medium text-gray-700 ${
                  needsName ? 'mobile:hidden' : ''
                }`}
              >
                {t('CONTACT_INFO')}
              </p>
            )}
            <div className="flex mobile:flex-col">
              {needsEmail && (
                <FormInput
                  icon={<img src={emailIcon} width={20} height={20} alt="" />}
                  className={getInputClasses(
                    'email',
                    isMobile && !needsName && !needsPhone
                      ? 'standalone'
                      : isMobile && !needsName && needsPhone
                      ? 'top'
                      : isMobile && needsName && needsPhone
                      ? 'middle'
                      : isMobile && !needsPhone
                      ? 'bottom'
                      : !needsPhone
                      ? 'standalone'
                      : 'left'
                  )}
                  formRegister={createEmailRegister()}
                  name="email"
                  error={errors['email']}
                  placeholder="mail@example.com"
                  disabled={disableFields?.email}
                  verified={verifiedFields?.email}
                  customStyle={{ direction: 'ltr' }}
                />
              )}
              {needsPhone && (
                <PhoneFormInput
                  name="phone"
                  error={errors['phone']}
                  selectedCountry={country as any}
                  className={getInputClasses(
                    'phone',
                    isMobile && !needsName && !needsEmail
                      ? 'standalone'
                      : isMobile && (needsName || needsEmail)
                      ? 'bottom'
                      : !needsEmail
                      ? 'standalone'
                      : 'right'
                  )}
                  setSelectedCountry={setCountry}
                  phoneFormRegister={createPhoneRegister({
                    countryCode: country?.code!,
                  })}
                  placeholder="10 000 000 00"
                  disableDropdown={disableDropdown?.phone}
                  disabled={disableFields?.phone}
                  verified={verifiedFields?.phone}
                />
              )}
            </div>
            <div className="flex mobile:flex-col mobile:gap-1 mobile:pl-4">
              {errors['email'] && (
                <p className="w-1/2 mt-2 text-sm text-red-600 mobile:w-full mobile:mt-0">
                  {errors['email']?.message}
                </p>
              )}
              {errors['phone'] && (
                <p
                  className={`w-1/2 ${
                    needsEmail ? 'ml-auto' : ''
                  } mt-2 text-red-600 text-sm mobile:w-full mobile:mt-0 mobile:ml-0`}
                >
                  {errors['phone']?.message}
                </p>
              )}
            </div>
          </div>
        )}
        {collectShippingAddress && (
          <>
            {renderAddressSection(ADDRESS_TYPES.SHIPPING)}
            <ErrorList addressType={ADDRESS_TYPES.SHIPPING} />
          </>
        )}
        {collectShippingAddress && collectBillingAddress && (
          <div className="[&_label]:!text-gray-600 [&_label]:!leading-4">
            <FormCheckbox
              formRegister={register('use_same_as_billing', { value: true })}
              label={t('SAME_AS_BILLING_CHECKBOX')}
              name="use_same_as_billing"
            />
          </div>
        )}
        {collectBillingAddress && !watchFields['use_same_as_billing'] && (
          <>
            {renderAddressSection(ADDRESS_TYPES.BILLING)}
            <ErrorList addressType={ADDRESS_TYPES.BILLING} />
          </>
        )}
      </form>
    );
  }

  return (
    <div className="flex flex-col">
      {needsName && (
        <div className="flex-col w-full mb-4 mobile:mb-0">
          {!hideLabels && (
            <>
              <p className="mb-2 text-sm font-medium text-gray-700 mobile:hidden">
                {t('FULL_NAME')}
              </p>
              <p className="hidden mb-2 text-sm font-medium text-gray-700 mobile:block">
                {t('CONTACT_INFO')}
              </p>
            </>
          )}
          <FormInput
            formRegister={createFullNameRegister()}
            name="name"
            error={errors['name']}
            className={getInputClasses(
              'name',
              isMobile && (needsEmail || needsPhone) ? 'top' : 'standalone'
            )}
            placeholder={"ex. Abdullah Adel"}
            customStyle={{direction:langDirection}}
          />
          {errors['name'] && !isMobile && (
            <p className="w-1/2 mt-2 text-sm text-red-600">
              {errors['name']?.message}
            </p>
          )}
        </div>
      )}
      {(needsEmail || needsPhone) && (
        <div className="flex flex-col">
          {!hideLabels && (
            <p
              className={`mb-2 text-sm font-medium text-gray-700 ${
                needsName ? 'mobile:hidden' : ''
              }`}
            >
              {t('CONTACT_INFO')}
            </p>
          )}
          <div className="flex mobile:flex-col">
            {needsEmail && (
              <FormInput
                icon={<img src={emailIcon} width={20} height={20} alt="" />}
                className={getInputClasses(
                  'email',
                  isMobile && !needsName && !needsPhone
                    ? 'standalone'
                    : isMobile && !needsName && needsPhone
                    ? 'top'
                    : isMobile && needsName && needsPhone
                    ? 'middle'
                    : isMobile && !needsPhone
                    ? 'bottom'
                    : !needsPhone
                    ? 'standalone'
                    : 'left'
                )}
                formRegister={createEmailRegister()}
                name="email"
                error={errors['email']}
                placeholder="mail@example.com"
                disabled={disableFields?.email}
                verified={verifiedFields?.email}
                customStyle={{ direction: 'ltr' }}
              />
            )}
            {needsPhone && (
              <PhoneFormInput
                name="phone"
                error={errors['phone']}
                selectedCountry={country as any}
                className={getInputClasses(
                  'phone',
                  isMobile && !needsName && !needsEmail
                    ? 'standalone'
                    : isMobile && (needsName || needsEmail)
                    ? 'bottom'
                    : !needsEmail
                    ? 'standalone'
                    : 'right'
                )}
                setSelectedCountry={setCountry}
                phoneFormRegister={createPhoneRegister({
                  countryCode: country?.code!,
                })}
                placeholder="10 000 000 00"
                disableDropdown={disableDropdown?.phone}
                disabled={disableFields?.phone}
                verified={verifiedFields?.phone}
              />
            )}
          </div>
          <div className="flex mobile:flex-col mobile:gap-1 mobile:pl-4">
            {errors['name'] && isMobile && (
              <p className="w-full mt-2 text-sm text-red-600">
                {errors['name']?.message}
              </p>
            )}
            {errors['email'] && (
              <p className="w-1/2 mt-2 text-sm text-red-600 mobile:w-full mobile:mt-0">
                {errors['email']?.message}
              </p>
            )}
            {errors['phone'] && (
              <p
                className={`w-1/2 ${
                  needsEmail ? 'ml-auto' : ''
                } mt-2 text-red-600 text-sm mobile:w-full mobile:mt-0 mobile:ml-0`}
              >
                {errors['phone']?.message}
              </p>
            )}
          </div>
        </div>
      )}
      {needsPurpose && (
        <div className="mt-4">
          <p className="mb-2 text-gray-700 english-label-medium font-Inter">
            {t('NOTES_PURPOSE_TITLE')}
          </p>
          <FormInput
            name="purpose"
            className={getInputClasses(
              'purpose',
              // isMobile && (needsEmail || needsPhone) ? 'top' : 'standalone'
              'standalone'
            )}
            formRegister={createPurposeRegister()}
            placeholder="e.g. Buying flight tickets"
            error={errors['purpose']}
            customStyle={{ direction: 'ltr' }}
          />
          {errors['purpose'] && (
            <p className="w-1/2 mt-2 text-sm text-red-600 mobile:pl-4">
              {errors['purpose']?.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
});
